import { onlineManager } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';

/** A browser upload straight to storage failed (not an API answer). */
export class FileUploadError extends Error {
  constructor(
    message = 'The file could not be uploaded. Please try again.',
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'FileUploadError';
  }
}

export function resolveApiErrorMessage(error: unknown, fallbackText: string): string {
  if (error instanceof Error) return error.message || fallbackText;
  if (typeof error === 'string') return error;
  return fallbackText;
}

export function isNetworkError(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response;
}

export function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

export function isConflictError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409;
}

export function isAccessDeniedError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 403;
}

export type ApiErrorKind =
  | 'offline'
  | 'unreachable'
  | 'server'
  | 'access-denied'
  | 'not-found'
  | 'date-order'
  | 'request';

export interface ApiErrorDisplay {
  kind: ApiErrorKind;
  title: string;
  description: string;
  status?: number | undefined;
}

const VALIDATION_ERROR_CODE = 'VALIDATION_ERROR';
const DATE_ORDER_MESSAGE_PATTERN = /^\w+ must not be before \w+$/;

function resolveStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

function resolveErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError<{ error?: string }>(error)) return undefined;
  return error.response?.data?.error;
}

function isDateOrderError(error: unknown): boolean {
  return error instanceof Error && DATE_ORDER_MESSAGE_PATTERN.test(error.message);
}

function describeFor(error: unknown, fallbackDescription: string): string {
  const errorCode = resolveErrorCode(error);
  if (errorCode === undefined || errorCode === VALIDATION_ERROR_CODE) return fallbackDescription;
  return resolveApiErrorMessage(error, fallbackDescription);
}

interface ResolveApiErrorDisplayOptions {
  /** Show the backend message for VALIDATION_ERROR instead of the generic text. */
  keepValidationMessage?: boolean;
}

export function resolveApiErrorDisplay(
  error: unknown,
  fallbackTitle: string,
  { keepValidationMessage = false }: ResolveApiErrorDisplayOptions = {},
): ApiErrorDisplay {
  if (error instanceof FileUploadError) {
    return { kind: 'request', title: 'Upload failed', description: error.message };
  }
  if (isNetworkError(error)) {
    return onlineManager.isOnline()
      ? {
          kind: 'unreachable',
          title: 'Cannot reach the server',
          description: 'The server is not responding. Please try again in a moment.',
        }
      : {
          kind: 'offline',
          title: 'You are offline',
          description: 'Check your internet connection, then try again.',
        };
  }

  const status = resolveStatus(error);

  if (isAccessDeniedError(error)) {
    return {
      kind: 'access-denied',
      title: 'You do not have access to this',
      description: describeFor(error, 'Ask your admin if you need it.'),
      status,
    };
  }

  if (isNotFoundError(error)) {
    return {
      kind: 'not-found',
      title: 'Not found',
      description: describeFor(error, 'This item may have been removed.'),
      status,
    };
  }

  if (!axios.isAxiosError(error) || (status !== undefined && status >= 500)) {
    return {
      kind: 'server',
      title: 'The server had a problem',
      description: 'Nothing is wrong on your side. Try again, and tell the team if it keeps up.',
      status,
    };
  }

  if (resolveErrorCode(error) === VALIDATION_ERROR_CODE) {
    if (isDateOrderError(error)) {
      return {
        kind: 'date-order',
        title: 'The dates are in the wrong order',
        description: 'The From date must be on or before the To date. Change one of them.',
        status,
      };
    }

    const genericDescription = 'Please check what you entered and try again.';
    return {
      kind: 'request',
      title: 'Some details are not valid',
      description: keepValidationMessage
        ? resolveApiErrorMessage(error, genericDescription)
        : genericDescription,
      status,
    };
  }

  return {
    kind: 'request',
    title: fallbackTitle,
    description: describeFor(error, 'Please try again.'),
    status,
  };
}

export function showApiErrorToast(error: unknown, fallbackText: string): void {
  if (isNetworkError(error)) return;
  const { title, description } = resolveApiErrorDisplay(error, fallbackText);
  toast.error(title, { description });
}
